import SEOHead from "@/components/SEOHead";
import DecarbonwizeNavbar from "@/components/landing/DecarbonwizeNavbar";
import Footer from "@/components/landing/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import DecarbonwizeFeaturesSection from "@/components/landing/DecarbonwizeFeaturesSection";
import DecarbonwizeHowItWorksSection from "@/components/landing/DecarbonwizeHowItWorksSection";
import DecarbonwizeFAQSection from "@/components/landing/DecarbonwizeFAQSection";
import DecarbonwizeCTASection from "@/components/landing/DecarbonwizeCTASection";

const Decarbonwize = () => {
  const { t, language } = useLanguage();

  const metadata = {
    en: {
      title: "Decarbonwize | Accelerate Your Net-Zero Journey",
      description: "Decarbonwize helps organizations set science-based targets and implement effective decarbonization strategies to achieve net-zero emissions.",
      locale: "en_US"
    },
    th: {
      title: "Decarbonwize | เร่งเส้นทางสู่ Net-Zero ขององค์กร",
      description: "Decarbonwize ช่วยองค์กรกำหนดเป้าหมายตามหลักวิทยาศาสตร์ และวางแผนกลยุทธ์ลดคาร์บอนอย่างมีประสิทธิภาพเพื่อบรรลุ Net-Zero",
      locale: "th_TH"
    }
  };

  const ogTags = {
    title: "Carbonwize | Audit-Grade Carbon Management for Enterprises",
    description: "Automate carbon accounting with enterprise-grade compliance, precision, and automation. Trusted by 500+ enterprises."
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={metadata[language].title}
        description={metadata[language].description}
        path="/decarbonwize"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://carbonwize.io"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Decarbonwize",
              "item": "https://carbonwize.io/decarbonwize"
            }
          ]
        }}
      />

      <DecarbonwizeNavbar />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#166a9c]/10 via-background to-[#3a3631]/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block px-4 py-2 rounded-full bg-[#166a9c]/10 text-[#166a9c] text-sm font-medium mb-6">
              Net-Zero Solutions
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
              Accelerate Your Journey to{" "}
              <span className="text-[#166a9c]">Net-Zero</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Set science-based targets, model reduction pathways, and track your progress towards a sustainable future with Decarbonwize.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-[#166a9c] hover:bg-[#166a9c]/90 text-white" asChild>
                <Link to="/contact-us">
                  Get Started
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="border-[#166a9c] text-[#166a9c] hover:bg-[#166a9c]/10">
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Decarbonwize Section */}
      <DecarbonwizeFeaturesSection />

      {/* How It Works Section */}
      <DecarbonwizeHowItWorksSection />

      {/* FAQ Section */}
      <DecarbonwizeFAQSection />

      {/* Pricing / CTA Section */}
      <DecarbonwizeCTASection />

      <Footer />
    </div>
  );
};

export default Decarbonwize;
